import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentStatusBar from '../components/upload/AgentStatusBar';
import SuggestionCards from '../components/upload/SuggestionCards';
import ChatStreamHistory, { ChatMessage } from '../components/upload/ChatStreamHistory';
import DropzoneComposer from '../components/upload/DropzoneComposer';
import { useUploadInvoice } from '../api/useUploadInvoice';

export default function UploadPage() {
  const navigate = useNavigate();
  const uploadInvoice = useUploadInvoice();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSelectPrompt = (promptText: string) => {
    setSelectedPrompt(promptText);
  };

  const handleSendMessage = async (text: string, file: File | null) => {
    setErrorMessage(null);
    const userMsgId = `user-${Date.now()}`;
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const userMessage: ChatMessage = {
      id: userMsgId,
      sender: 'user',
      text: text || 'Please process and extract fields from the attached invoice.',
      timestamp: now,
      fileName: file?.name,
    };

    const agentProcessingId = `agent-${Date.now()}`;
    const agentProcessingMessage: ChatMessage = {
      id: agentProcessingId,
      sender: 'agent',
      text: file
        ? 'Uploading and analyzing document with AI agent (6–8s parsing)…'
        : 'Thinking…',
      timestamp: now,
      isProcessing: true,
    };

    setMessages((prev) => [...prev, userMessage, agentProcessingMessage]);
    setSelectedPrompt('');

    if (file) {
      try {
        const result = await uploadInvoice.mutateAsync(file);

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === agentProcessingId
              ? {
                  ...msg,
                  isProcessing: false,
                  text: `✅ Document received and parsed successfully!\n\n• Token: ${result.id}\n• Total Pages: ${result.total_pages}\n• Status: ${result.status.label}\n\nRedirecting to invoice analysis view...`,
                }
              : msg
          )
        );

        setTimeout(() => {
          navigate(`/invoices/${result.id}`);
        }, 800);
      } catch (err: unknown) {
        let msg = 'Failed to process document. Please try again.';
        if (err instanceof Error) {
          msg = err.message;
        }

        setErrorMessage(msg);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === agentProcessingId
              ? {
                  ...m,
                  isProcessing: false,
                  text: `❌ Error: ${msg}`,
                }
              : m
          )
        );
      }
    } else {
      // Text only prompt response
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === agentProcessingId
              ? {
                  ...msg,
                  isProcessing: false,
                  text: `I am ready to help! Please drag and drop or attach a PDF, JPG, or JPEG invoice document so I can extract all line items and tax data.`,
                }
              : msg
          )
        );
      }, 700);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-2 sm:py-4">
      {/* Agent Status Bar */}
      <AgentStatusBar />

      {/* Smart Prompt Suggestions */}
      <SuggestionCards onSelectPrompt={handleSelectPrompt} />

      {/* Chat Stream Stream / Welcome Area */}
      <ChatStreamHistory messages={messages} />

      {/* Composer & Drag Dropzone */}
      <DropzoneComposer
        onSendMessage={handleSendMessage}
        isSubmitting={uploadInvoice.isPending}
        initialPrompt={selectedPrompt}
        errorMessage={errorMessage}
      />
    </div>
  );
}
